/**
 * FilterDrawer Component
 * Mobile filter sheet for customer orders
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-007
 */

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import type { CustomerOrderFilters } from '../types/order.types';
import type { OrderStatus } from '@/types/order';

// ============================================
// STATUS OPTIONS
// ============================================

const STATUS_OPTIONS: { value: OrderStatus; label: string; description: string }[] = [
  { value: 'PENDING', label: 'Pending', description: 'Awaiting confirmation' },
  { value: 'CONFIRMED', label: 'Confirmed', description: 'Order confirmed' },
  { value: 'PROCESSING', label: 'Processing', description: 'Being processed' },
  { value: 'READY', label: 'Ready', description: 'Ready for dispatch' },
  { value: 'DISPATCHED', label: 'Dispatched', description: 'Out for delivery' },
  { value: 'DELIVERED', label: 'Delivered', description: 'Successfully delivered' },
  { value: 'CANCELLED', label: 'Cancelled', description: 'Order cancelled' },
];

// ============================================
// COMPONENT PROPS
// ============================================

export interface FilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: CustomerOrderFilters;
  onStatusToggle: (status: OrderStatus) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

// ============================================
// COMPONENT
// ============================================

/**
 * FilterDrawer Component
 * Mobile sheet for filtering orders
 *
 * @example
 * ```tsx
 * <FilterDrawer
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   filters={filters}
 *   onStatusToggle={toggleStatus}
 *   onApplyFilters={applyFilters}
 *   onClearFilters={clearFilters}
 * />
 * ```
 */
export function FilterDrawer({
  open,
  onOpenChange,
  filters,
  onStatusToggle,
  onApplyFilters,
  onClearFilters,
}: FilterDrawerProps) {
  const selectedStatuses = filters.statuses || [];

  const handleApply = () => {
    onApplyFilters();
    onOpenChange(false);
  };

  const handleClear = () => {
    onClearFilters();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh]">
        <SheetHeader>
          <SheetTitle>Filter Orders</SheetTitle>
          <SheetDescription>
            Filter your orders by status and other criteria
          </SheetDescription>
        </SheetHeader>

        <div className="py-6 space-y-6 overflow-y-auto max-h-[calc(85vh-180px)]">
          {/* Status Filter Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-slate-900">Order Status</h3>
            <div className="space-y-3">
              {STATUS_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-start space-x-3">
                  <Checkbox
                    id={`status-${option.value}`}
                    checked={selectedStatuses.includes(option.value)}
                    onCheckedChange={() => onStatusToggle(option.value)}
                  />
                  <div className="grid gap-1 leading-none">
                    <Label
                      htmlFor={`status-${option.value}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {option.label}
                    </Label>
                    <p className="text-xs text-slate-500">
                      {option.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <SheetFooter className="absolute bottom-0 left-0 right-0 p-6 bg-white border-t">
          <div className="flex gap-3 w-full">
            <Button
              variant="outline"
              onClick={handleClear}
              className="flex-1"
            >
              Clear All
            </Button>
            <Button
              onClick={handleApply}
              className="flex-1"
            >
              Apply Filters
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default FilterDrawer;
